import React from "react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import { ASSET } from "@/lib/api";
import {
  Award,
  HeartHandshake,
  Sparkles,
  Shield,
  Compass,
  Quote,
} from "lucide-react";

const VALUE_ICONS = {
  award: Award,
  "heart-handshake": HeartHandshake,
  sparkles: Sparkles,
  shield: Shield,
};

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
      <span className="h-px w-6 bg-teal" />
      {children}
    </span>
  );
}

export default function About() {
  const { data: page } = useFetch("/pages/about");

  if (!page) return <div className="min-h-screen" />;

  const story = page.story || {};
  const values = page.values || [];
  const audience = page.audience || [];
  const challenges = page.challenges || [];
  const solutions = page.solutions || [];

  return (
    <>
      <SEO
        title={page.seo?.title}
        description={page.seo?.description}
        path="/about"
      />

      {/* HERO */}
      <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 bg-navy text-white overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src={ASSET(page.image || "/api/media/og-image.png")}
            alt=""
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
          <div className="img-grain absolute inset-0" />
        </div>
        <div className="container relative">
          <Reveal>
            <Eyebrow>{story.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              data-testid="about-title"
              className="mt-6 font-display font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl"
            >
              {story.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-white/80">
              {story.body}
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-mission-vision">
        <div className="container grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="h-full p-10 lg:p-12 rounded-3xl bg-soft-gray border border-navy-50">
              <Compass className="h-7 w-7 text-teal" />
              <h2 className="mt-5 font-display font-semibold text-2xl text-navy-800">
                Our mission
              </h2>
              <p className="mt-5 text-base leading-relaxed text-navy-700/80">
                {page.mission}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full p-10 lg:p-12 rounded-3xl bg-navy text-white relative overflow-hidden">
              <div className="img-grain absolute inset-0" />
              <div className="relative">
                <Sparkles className="h-7 w-7 text-teal-light" />
                <h2 className="mt-5 font-display font-semibold text-2xl">Our vision</h2>
                <p className="mt-5 text-base leading-relaxed text-white/80">
                  {page.vision}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-soft-gray py-24 lg:py-32" data-testid="about-values">
        <div className="container">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>Our core values</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl tracking-tight text-navy-800">
                The principles that guide every decision.
              </h2>
            </Reveal>
          </div>
          <Stagger className="mt-14 grid gap-5 md:grid-cols-2">
            {values.map((v) => {
              const Icon = VALUE_ICONS[v.icon] || Award;
              return (
                <Item key={v.title}>
                  <div className="p-8 rounded-2xl bg-white border border-navy-50 h-full hover:shadow-soft transition-shadow">
                    <div className="flex items-start gap-5">
                      <span className="text-5xl font-display font-bold text-teal/30 leading-none">
                        {v.number}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-teal" />
                          <h3 className="font-display font-semibold text-xl text-navy-800">
                            {v.title}
                          </h3>
                        </div>
                        <p className="mt-3 text-base leading-relaxed text-navy-700/80">
                          {v.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* CHALLENGES */}
      {challenges.length > 0 && (
        <section className="bg-white py-24 lg:py-32" data-testid="about-challenges">
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <Reveal><Eyebrow>The healthcare challenge</Eyebrow></Reveal>
                <Reveal delay={0.05}>
                  <h2 className="mt-5 font-display font-semibold text-3xl md:text-4xl tracking-tight text-navy-800">
                    Realities of healthcare in Somalia today.
                  </h2>
                </Reveal>
              </div>
              <Stagger className="lg:col-span-8 grid gap-5">
                {challenges.map((c) => (
                  <Item key={c.title}>
                    <div className="p-7 rounded-2xl border-l-4 border-teal bg-soft-gray">
                      <div className="flex items-center gap-3">
                        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-navy-500">
                          {c.number}
                        </span>
                        <h3 className="font-display font-semibold text-lg text-navy-800">
                          {c.title}
                        </h3>
                      </div>
                      <p className="mt-3 text-base leading-relaxed text-navy-700/80">
                        {c.description}
                      </p>
                    </div>
                  </Item>
                ))}
              </Stagger>
            </div>
          </div>
        </section>
      )}

      {/* SOLUTIONS */}
      {solutions.length > 0 && (
        <section className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden" data-testid="about-solutions">
          <div className="img-grain absolute inset-0" />
          <div className="container relative">
            <div className="max-w-3xl">
              <Eyebrow>Our solution</Eyebrow>
              <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl tracking-tight">
                How IMG closes the healthcare gap.
              </h2>
            </div>
            <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
              {solutions.map((s) => (
                <Item key={s.title}>
                  <div className="p-8 rounded-2xl bg-white/[0.04] border border-white/10 h-full backdrop-blur-sm">
                    <h3 className="font-display font-semibold text-xl text-teal-light">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-white/75">
                      {s.description}
                    </p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* TARGET AUDIENCE */}
      {audience.length > 0 && (
        <section className="bg-soft-gray py-24 lg:py-32" data-testid="about-audience">
          <div className="container">
            <Reveal><Eyebrow>Who we serve</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl tracking-tight text-navy-800 max-w-3xl">
                Care designed for the people who need it most.
              </h2>
            </Reveal>
            <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
              {audience.map((a) => (
                <Item key={a.title}>
                  <div className="p-8 rounded-2xl bg-white border border-navy-50 h-full">
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-teal">
                      Audience {a.number}
                    </span>
                    <h3 className="mt-3 font-display font-semibold text-xl text-navy-800">
                      {a.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-navy-700/80">
                      {a.description}
                    </p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* PULL QUOTE */}
      <section className="bg-cream py-24 lg:py-32" data-testid="about-quote">
        <div className="container max-w-4xl text-center">
          <Reveal>
            <Quote className="h-10 w-10 text-teal mx-auto" />
          </Reveal>
          <Reveal delay={0.05}>
            <blockquote className="mt-8 font-display font-medium text-3xl md:text-5xl leading-[1.15] tracking-tight text-navy-800">
              Expert medical care, delivered wherever you are.
            </blockquote>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-xs uppercase tracking-[0.32em] font-semibold text-teal">
              World-class care &middot; Close to home
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
